import {
  AiModelEnum,
  getSampleVariantFromMaterial,
  type MaterialProductInfoDetails,
  type SampleVariant,
  type SiteMaterialSpecificationDTO,
  useApiTradeV2SiteMaterialsQualityReportRetrieve,
} from 'api';

const siteDataKeys: (keyof SiteMaterialSpecificationDTO)[] = [
  'storage_conditions',
  'input_material_notes',
  'machines_used_notes',
  'stored_on',
  'production_volume',
  'site_notes',
];

const loadingDataKeys: (keyof SiteMaterialSpecificationDTO)[] = ['loading_technique', 'loading_notes', 'packed_on'];

const materialSpecificationKeys: (keyof SiteMaterialSpecificationDTO)[] = [
  'storage_type',
  'material_strapping',
  'height',
  'width',
  'length',
  'weight',
  'visual_analysis',
  'radiation_analysis',
  'ferrous_analysis',
];

const materialProductKeysForSpecification: (keyof MaterialProductInfoDetails)[] = [
  'has_caps_and_labels',
  'is_food_grade',
];

const hasEnoughData = (keys: (keyof SiteMaterialSpecificationDTO)[], data?: SiteMaterialSpecificationDTO) =>
  keys.some((key) => Boolean(data?.[key]));

const hasEnoughProductData = (keys: (keyof MaterialProductInfoDetails)[], data?: MaterialProductInfoDetails) =>
  keys.some((key) => Boolean(data?.[key]));

const ALLOWED_GRAVIMETRIC_MATERIALS: SampleVariant[] = ['alu'];

export const useQualityReportData = (siteMaterialId: string) => {
  const {
    data,
    isLoading: siteMaterialDataIsLoading,
    error,
  } = useApiTradeV2SiteMaterialsQualityReportRetrieve(siteMaterialId, {
    query: {
      enabled: Boolean(siteMaterialId),
      retry: false,
    },
  });

  const sampleMaterial = getSampleVariantFromMaterial(data?.material);

  const hasEnoughSiteData = hasEnoughData(siteDataKeys, data?.specification);
  const hasEnoughLoadingData = hasEnoughData(loadingDataKeys, data?.specification);
  const hasEnoughMaterialSpecificationData =
    hasEnoughData(materialSpecificationKeys, data?.specification) ||
    hasEnoughProductData(materialProductKeysForSpecification, data?.material_product ?? undefined);
  const hasEnoughAiSurfaceAnalysisData =
    Object.keys(data?.composition ?? {}).length > 0 && data?.ai_model === AiModelEnum.pet;
  const hasEnoughAiGravimetricAnalysisData =
    Object.keys(data?.ai_gravimetric_composition ?? {}).length > 0 &&
    Object.keys(data?.ai_gravimetric_sample ?? {}).length > 0 &&
    sampleMaterial &&
    ALLOWED_GRAVIMETRIC_MATERIALS.includes(sampleMaterial);

  return {
    data,
    error,
    hasEnoughAiGravimetricAnalysisData,
    hasEnoughAiSurfaceAnalysisData,
    hasEnoughLoadingData,
    hasEnoughMaterialSpecificationData,
    hasEnoughSiteData,
    isLoading: siteMaterialDataIsLoading,
    sampleMaterial,
  };
};
